// @flow
import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';

import { ID_MODAL_HEADER__SHOPPING__WEBCALLBACK_TRIGGER } from 'Components/utils/identifiers/constants';
import { t } from 'Src/i18n';
import ReactAppContext from 'Context/ReactAppContext';
import { ReactPageContext } from 'Context/pageContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { WEB_CALLBACK_SUBMIT } from 'Tracking/constants';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';

import { Button } from 'Components/frameworks/button/Button.jsx';
import { Notification } from 'Components/frameworks/notification/Notification.jsx';

import { GroupedSelect, GroupedInput } from './WebCallbackFields.jsx';
import {
  ASAP_TIME,
  CALLBACK_STATUS,
  PHONE_PREFIXES,
  getTimeSlots,
  fetchOpeningHours,
  requestCallback,
} from './WebCallbackForm.utils';

const DEFAULT_OPENING_TIME = '10:00:00';
const DEFAULT_CLOSING_TIME = '18:00:00';
const DEFAULT_OPENING_TIMES_PER_DAY = {
  mon: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  tue: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  wed: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  thu: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  fri: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  sat: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
  sun: [
    {
      from: DEFAULT_OPENING_TIME,
      to: DEFAULT_CLOSING_TIME,
    },
  ],
};

type WebCallbackFormProps = {
  callbackStatus: string,
  setCallbackStatus: string => void,
  trackingData: {|
    actionGA: string,
  |},
};

export function WebCallbackForm({
  callbackStatus,
  setCallbackStatus,
  trackingData,
}: WebCallbackFormProps) {
  const appContext = useContext(ReactAppContext);
  const pageContext = useContext(ReactPageContext);
  const track = useTracking();

  // These are UTC times, like what Freespee returns in from_time, to_time.
  const [openingTimesPerDay, setOpeningTimesPerDay] = useState(DEFAULT_OPENING_TIMES_PER_DAY);
  const timeSlots = useMemo(
    () =>
      getTimeSlots({
        openingTimesPerDay,
        i18n: {
          asap: t('common.dateTime.asap'),
          today: t('common.dateTime.today'),
          tomorrow: t('common.dateTime.tomorrow'),
        },
      }),
    [openingTimesPerDay],
  );
  const [initialDay] = timeSlots;
  const [currentDay, setCurrentDay] = useState(initialDay);

  useEffect(() => {
    fetchOpeningHours({ setOpeningTimesPerDay }, appContext);
  }, []);

  useEffect(
    () => {
      const [newDay] = timeSlots;
      setCurrentDay(newDay);
    },
    [timeSlots],
  );

  if (!currentDay) return null;

  const localPhonePrefix = PHONE_PREFIXES.find(prefix => {
    const country = appContext.locale.split('-')[1];
    return country === prefix.countryCode;
  });
  const initialPhonePrefix =
    localPhonePrefix || PHONE_PREFIXES.find(prefix => prefix.countryCode === 'FR') || {};

  const changeDay = (event, setFieldValue) => {
    const dayValue = event.target.value;
    const newDay = timeSlots.find(slot => slot.value === dayValue);
    if (newDay) {
      setCurrentDay(newDay);
      setFieldValue('slotDay', newDay.value, false);
    }
  };

  return (
    <Formik
      initialValues={{
        slotDay: currentDay.value,
        slotTime: currentDay?.slots?.[0]?.value,
        phonePrefix: initialPhonePrefix.value,
        phoneNumber: '',
      }}
      validate={({ phoneNumber }) => {
        const errors = {};
        if (!phoneNumber) {
          errors.phoneNumber = 'Phone number is required';
        }
        // https://www.twilio.com/docs/glossary/what-e164
        if (!/^[1-9]\d{1,14}$/.test(phoneNumber)) {
          errors.phoneNumber = 'Phone number is not valid';
        }
        return errors;
      }}
      onSubmit={(formValues, { setSubmitting }) => {
        const { slotTime, phonePrefix, phoneNumber } = formValues;
        requestCallback({
          scheduled: slotTime === ASAP_TIME ? null : slotTime,
          requesterNumber: `+${phonePrefix}${phoneNumber}`,
          requesterNumberPrefix: phonePrefix,
          setCallbackStatus,
          setSubmitting,
        });
        track({
          categoryGA: pageContext.pageId,
          labelGA: WEB_CALLBACK_SUBMIT,
          ...trackingData,
        });
      }}
    >
      {({ isValid, isSubmitting, setFieldValue }) => {
        const isDisabled = callbackStatus !== CALLBACK_STATUS.UNSCHEDULED || !isValid;
        return (
          <Form data-testid="web-callback-form" className="h-full">
            <div className="flex flex-col justify-between h-full w-full bg-gray-200">
              <div
                className="w-full bg-ardoise"
                style={{
                  backgroundImage: `url("${t('webCallback.picture')}")`,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  height: '150px',
                }}
              />
              <div className="flex-auto px-6 py-5">
                <h3 className="my-0" id={ID_MODAL_HEADER__SHOPPING__WEBCALLBACK_TRIGGER}>
                  {t('webCallback.title')}
                </h3>
                <p>{t('webCallback.subtitle')}</p>
                {callbackStatus !== CALLBACK_STATUS.UNSCHEDULED ? (
                  <Notification
                    color={
                      callbackStatus === CALLBACK_STATUS.SCHEDULED ? COLORS.SUCCESS : COLORS.WARNING
                    }
                    hasBorder
                    icon={
                      callbackStatus === CALLBACK_STATUS.SCHEDULED
                        ? ICONS.SMALL_CIRCLE_CHECK
                        : ICONS.CIRCLED_CROSS
                    }
                    iconWidth="1rem"
                  >
                    <p
                      className={`m-0 text-base text-${
                        callbackStatus === CALLBACK_STATUS.SCHEDULED
                          ? COLORS.SUCCESS
                          : COLORS.WARNING
                      }`}
                    >
                      {callbackStatus === CALLBACK_STATUS.SCHEDULED
                        ? t('webCallback.successMessage')
                        : t('webCallback.errorMessage')}
                    </p>
                  </Notification>
                ) : (
                  <Fragment>
                    <div className="inline-block w-full md:w-3/5 mb-3 md:pr-2">
                      <label className="block w-full p-1 text-sm">
                        {t('webCallback.timeLabel')}
                      </label>
                      <GroupedSelect
                        fieldName="slotDay"
                        position="left"
                        width="2/5"
                        // $FlowFixMe
                        options={timeSlots}
                        onChange={e => changeDay(e, setFieldValue)}
                      />
                      <GroupedSelect
                        fieldName="slotTime"
                        position="right"
                        width="3/5"
                        // $FlowFixMe
                        options={currentDay.slots}
                      />
                    </div>
                    <div className="inline-block w-full md:w-2/5 mb-3">
                      <label className="block w-full p-1 text-sm">
                        {t('webCallback.phoneLabel')}
                      </label>
                      <GroupedSelect
                        fieldName="phonePrefix"
                        position="left"
                        width="1/2"
                        options={PHONE_PREFIXES}
                      />
                      <GroupedInput
                        fieldName="phoneNumber"
                        position="right"
                        width="1/2"
                        type="tel"
                        required
                      />
                    </div>
                  </Fragment>
                )}
              </div>
              <div
                className="flex justify-end px-6 w-full"
                style={{
                  minHeight: '3.75rem',
                }}
              >
                {callbackStatus === CALLBACK_STATUS.UNSCHEDULED ? (
                  <Button
                    className="self-center w-full md:w-1/2 text-center"
                    type="submit"
                    isLoading={isSubmitting}
                    isDisabled={isDisabled}
                  >
                    {t('webCallback.callToAction')}
                  </Button>
                ) : null}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
